import React, { useState, useCallback } from 'react';

// Hooks
import { useOnboardingState } from 'Shared/utils/hooks/use-onboarding-state';
import { useToggle } from 'Shared/utils/hooks';

// Actions
import { generateAgendaAiSuggestionsManual } from 'Shared/actions/onboardingState';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpButton } from 'Wpds/components/button';
import OnboardingSlackTitle from './OnboardingSlackTitle';
import WpTextField from 'Wpds/components/text-field/WpTextField';

// Styles
import './OnboardingSlackAIGenerateManual.scss';

const OnboardingSlackAIGenerateManual = () => {
  const { onboardingState, handleAdvanceOnboardingState, handleAdvanceBackOnboardingState } = useOnboardingState();

  const [meetingGoals, setMeetingGoals] = useState('');
  const [showAgenda, setShowAgenda] = useState(false);

  const [isGenerating, toggleIsGenerating] = useToggle(false);

  const [discussionTopics, setDiscussionTopics] = useState<string[]>([]);
  const [actionItems, setActionItems] = useState<string[]>([]);

  const workspaceName = onboardingState?.workspaces[0]?.name;
  const hasGeneratedAgenda = discussionTopics.length > 0 || actionItems.length > 0;

  const handleGenerateAgendaClick = () => {
    toggleIsGenerating();

    generateAgendaAiSuggestionsManual({
      meetingGoals,
    }).then(response => {
      if (response.status === 200 && Object.keys(response.data).length) {
        setDiscussionTopics(response.data.discussion_topics);
        setActionItems(response.data.action_items);
        setShowAgenda(true);
      }
      toggleIsGenerating();
    });
  };

  const handleNextClick = () => {
    handleAdvanceOnboardingState({
      onboardingState: {
        agenda_from_chat: {
          discussion_topics: discussionTopics,
          action_items: actionItems,
        },
      },
    });
  };

  const handleRemoveDiscussionTopic = useCallback(
    item => {
      setDiscussionTopics(discussionTopics.filter(topic => topic !== item));
    },
    [discussionTopics, setDiscussionTopics]
  );

  const handleRemoveActionItem = useCallback(
    item => {
      setActionItems(actionItems.filter(ai => ai !== item));
    },
    [actionItems, setActionItems]
  );

  const handleHideAgenda = () => {
    setShowAgenda(false);
  };

  const handleShowAgenda = () => {
    setShowAgenda(true);
  };

  const baseClassName = 'OnboardingSlackAIGenerateManual';

  const anyInputs = meetingGoals.trim();

  return (
    <div className={baseClassName}>
      <OnboardingSlackTitle
        onBack={showAgenda ? handleHideAgenda : handleAdvanceBackOnboardingState}
        onNext={showAgenda ? handleNextClick : hasGeneratedAgenda && handleShowAgenda}
        title="Generate Agenda"
      />
      <div>
        {!showAgenda && (
          <>
            <p className={`${baseClassName}__desription`}>
              What do you want to get out of your {workspaceName} meeting?
            </p>
            <div className={`${baseClassName}__textfield`}>
              <WpTextField
                id="meeting_goals"
                label="What are the goals of this meeting?"
                placeholder="Ex: Align on priorities and blockers, align on Q4 Sales Strategy"
                value={meetingGoals}
                onChange={setMeetingGoals}
                multiline
              />
            </div>
          </>
        )}
        <div className={`${baseClassName}__button`}>
          <WpButton
            block
            onClick={handleGenerateAgendaClick}
            loading={isGenerating}
            variant={hasGeneratedAgenda ? 'secondary' : 'primary'}
            disabled={!anyInputs}
            size="lg"
          >
            {hasGeneratedAgenda ? 'Regenerate' : 'Generate'} Agenda
          </WpButton>
        </div>
      </div>

      {showAgenda && (
        <div className={`${baseClassName}__print-out-section`}>
          <p className={`${baseClassName}__print-out-section__title`}>Discussion Topics</p>
          <ul className={`${baseClassName}__print-out-section__list`}>
            {discussionTopics.map((item, index) => (
              <li key={`discussion-topic-${index}`}>
                <div className={`${baseClassName}__print-out-section__list__item`}>
                  <span className={`${baseClassName}__print-out-section__list__item__desc`}>{item}</span>
                  <div>
                    <WpButton size="sm" variant="tertiary" onClick={() => handleRemoveDiscussionTopic(item)}>
                      <WpIcon name="times" />
                    </WpButton>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {actionItems.length > 0 && (
        <div className={`${baseClassName}__print-out-section`}>
          <p className={`${baseClassName}__print-out-section__title`}>Action Items</p>
          <ul className={`${baseClassName}__print-out-section__list`}>
            {actionItems.map((item, index) => (
              <li key={`action-item-${index}`}>
                <div className={`${baseClassName}__print-out-section__list__item`}>
                  <span className={`${baseClassName}__print-out-section__list__item__desc`}>{item}</span>
                  <div>
                    <WpButton size="sm" variant="tertiary" onClick={() => handleRemoveActionItem(item)}>
                      <WpIcon name="times" />
                    </WpButton>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OnboardingSlackAIGenerateManual;
